import React, { useState, useEffect } from "react";
import { Pen, Eye, EyeOff } from "lucide-react";
import { GOLD } from "../constants/theme";
import { supabase } from "../../supabaseClient";

export function MyProfilePanel({C, dark, onClose, profile, onProfileUpdate, photo, onPhotoUpdate, dismissedBanners, dismissBanner, sbUser, onError, userStats, go, onEditProfile}){
  const [bioExpanded, setBioExpanded] = useState(false);
  const [pwModal, setPwModal] = useState(false);
  const [pwForm, setPwForm] = useState({current:"",newPw:"",confirm:""});
  const [pwErrors, setPwErrors] = useState({});
  const [showPw, setShowPw] = useState(false);
  const [pwSuccess, setPwSuccess] = useState(false);
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailDraft, setEmailDraft] = useState(profile.email||"");
  const [emailError, setEmailError] = useState("");
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [profileStatsLoaded, setProfileStatsLoaded] = useState(false);
  useEffect(()=>{const t=setTimeout(()=>setProfileStatsLoaded(true),1200);return()=>clearTimeout(t);},[]);

  // Escape key closes the panel
  useEffect(()=>{
    const onKey = (e) => { if (e.key === "Escape" && !pwModal && !editingEmail) onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  },[onClose, pwModal, editingEmail]);

  const initials = profile.name?.split(" ").map(w=>w[0]||"").join("").toUpperCase()||"";

  // Shared by the email and password forms.
  const inputStyle = (hasError) => ({
    width:"100%",background:dark?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.05)",
    border:`1px solid ${hasError?"#E06060":dark?"rgba(226,221,159,0.3)":GOLD+"60"}`,borderRadius:8,
    padding:"8px 12px",fontSize:13,color:C.text,outline:"none",fontFamily:"inherit",boxSizing:"border-box"
  });

  const submitPw = async () => {
    const errs = {};
    if(pwForm.newPw.length<8) errs.newPw = "Password must be at least 8 characters";
    if(pwForm.confirm!==pwForm.newPw) errs.confirm = "Passwords do not match";
    setPwErrors(errs);
    if(Object.keys(errs).length>0) return;

    // Verify current password by attempting to sign in with it
    if(sbUser?.email){
      try{
        const { error: verifyErr } = await supabase.auth.signInWithPassword({
          email: sbUser.email,
          password: pwForm.current,
        });
        if(verifyErr){
          setPwErrors({current: "Current password is incorrect"});
          return;
        }
      }catch(e){
        setPwErrors({current: "Couldn't verify current password"});
        return;
      }
      // Update to new password
      try{
        const { error: updateErr } = await supabase.auth.updateUser({ password: pwForm.newPw });
        if(updateErr) throw updateErr;
      }catch(e){
 if(onError) onError("Couldn't update password: "+(e.message||"please try again"));
        return;
      }
    } else {
      if(onError) onError("You need to be signed in to change your password.");
      return;
    }

    setPwSuccess(true);
    setTimeout(()=>{setPwModal(false);setPwSuccess(false);setPwForm({current:"",newPw:"",confirm:""});setPwErrors({});},1800);
  };

  const saveEmail = async () => {
    const trimmed = emailDraft.trim();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
    if(!valid){ setEmailError("Please enter a valid email address"); return; }
    setEmailError("");

    if(sbUser?.id){
      try{
        const { error } = await supabase.auth.updateUser({ email: trimmed });
        if(error) throw error;
        // Supabase sends a confirmation email to the new address. Let the user know.
        onProfileUpdate({...profile, email:trimmed});
        setEditingEmail(false);
        setEmailSuccess(true);
        setTimeout(()=>setEmailSuccess(false),3500);
      }catch(e){
        setEmailError(e.message || "Couldn't update email");
      }
      return;
    }
    // Fallback: local-only update
    onProfileUpdate({...profile, email:trimmed});
    setEditingEmail(false);
    setEmailSuccess(true);
    setTimeout(()=>setEmailSuccess(false),2000);
  };

  const roleStr = (profile.role||"").toLowerCase();
  const showPlans = roleStr.includes("coach")||roleStr.includes("trainer")||!roleStr;
  // Real numbers from the same userStats the dashboard renders — this block
  // used to be hardcoded zeros behind a fake shimmer. Tiles with a real
  // destination navigate; the rest stay informational.
  const navTo = (dest) => { if (go) { onClose?.(); go(dest); } };
  const profileStats = [
    ["Community Posts", String(userStats?.posts ?? 0), () => navTo("community")],
    ["Comments Left", String(userStats?.comments ?? 0), () => navTo("community")],
    ...(showPlans?[["Plans Created", String(userStats?.plans ?? 0), () => navTo("plans")]]:[]),
    ["Day Streak", String(userStats?.streak ?? 1), null],
  ];

  return (
    <div onClick={onClose}
      style={{position:"fixed",inset:0,zIndex:3000,background:"rgba(0,0,0,0.55)",backdropFilter:"blur(3px)"}}>
      <div onClick={e=>e.stopPropagation()}
        style={{position:"absolute",right:0,top:0,bottom:0,width:400,maxWidth:"100vw",
          background:dark?"#111111":"#fff",
          borderLeft:`1px solid ${dark?"rgba(255,255,255,0.06)":C.border}`,
          display:"flex",flexDirection:"column",
          animation:"slideR .28s cubic-bezier(.22,1,.36,1)"}}>

        <div style={{padding:"20px 22px 16px",borderBottom:`1px solid ${dark?"rgba(255,255,255,0.06)":C.border}`,
          display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
          <div style={{fontSize:15,fontWeight:800,color:C.text}}>My Profile</div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <button onClick={()=>onEditProfile?.()} className="btn"
              style={{fontSize:12,fontWeight:700,padding:"6px 14px",borderRadius:7,
                background:dark?"rgba(226,221,159,0.12)":"rgba(0,0,0,0.06)",
                border:`1px solid ${dark?"rgba(226,221,159,0.25)":C.border}`,
                color:dark?GOLD:"#111",cursor:"pointer"}}>
              Edit profile
            </button>
            <div className="btn" onClick={onClose}
              style={{width:30,height:30,borderRadius:7,display:"flex",alignItems:"center",
                justifyContent:"center",background:dark?"rgba(255,255,255,0.06)":"rgba(0,0,0,0.06)",
                cursor:"pointer",fontSize:16,color:C.textDim}}>×</div>
          </div>
        </div>

        <div style={{flex:1,overflowY:"auto",padding:"22px"}}>

          <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:24}}>
            <div style={{position:"relative",flexShrink:0}}>
              <div style={{width:64,height:64,borderRadius:"50%",overflow:"hidden",
                background:GOLD,display:"flex",alignItems:"center",justifyContent:"center",
                fontSize:20,fontWeight:800,color:"#111",
                boxShadow:`0 0 0 3px ${dark?"#111111":"#fff"}, 0 0 0 4px ${GOLD}55`}}>
                {photo
                  ? <img src={photo} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                  : initials}
              </div>
              <div style={{position:"absolute",bottom:2,right:2,width:14,height:14,borderRadius:"50%",
                background:"#5AB584",border:`2px solid ${dark?"#111111":"#fff"}`}}/>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:17,fontWeight:800,color:C.text,marginBottom:3}}>{profile.name}</div>
              <div style={{fontSize:12,color:GOLD,fontWeight:600}}>{profile.handle}</div>
            </div>
          </div>

          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:22}}>
            {(profile.certs||[]).map((c,i)=>(
              <div key={i} style={{fontSize:10,fontWeight:700,padding:"4px 10px",borderRadius:20,
                background:i===0?GOLD:dark?"rgba(226,221,159,0.1)":"rgba(0,0,0,0.06)",
                color:i===0?"#111":GOLD,
                border:i===0?"none":`1px solid ${dark?"rgba(226,221,159,0.25)":GOLD+"50"}`,
                letterSpacing:.4}}>{c}</div>
            ))}
          </div>

          {[
            {label:"Role", field:"role"},
            {label:"Organization", field:"org"},
            {label:"Location", field:"location"},
          ].map(({label,field})=>{
            if(!profile[field]) return null;
            return (
              <div key={field} style={{marginBottom:14}}>
                <div style={{fontSize:10,fontWeight:700,color:dark?GOLD+"99":"#999",
                  letterSpacing:1.2,textTransform:"uppercase",marginBottom:5}}>{label}</div>
                <div style={{fontSize:13,color:C.text,fontWeight:500}}>{profile[field]}</div>
              </div>
            );
          })}

          {profile.bio && (
            <div style={{marginBottom:18}}>
              <div style={{fontSize:10,fontWeight:700,color:dark?GOLD+"99":"#999",
                letterSpacing:1.2,textTransform:"uppercase",marginBottom:5}}>Bio</div>
                <div>
                  <div style={{fontSize:13,color:C.textMid,lineHeight:1.6,
                    ...(bioExpanded?{}:{display:"-webkit-box",WebkitLineClamp:3,WebkitBoxOrient:"vertical",overflow:"hidden"})}}>
                    {profile.bio}
                  </div>
                  {profile.bio && profile.bio.length>120 && !bioExpanded && (
                    <div onClick={()=>setBioExpanded(true)}
                      style={{fontSize:12,color:GOLD,fontWeight:600,cursor:"pointer",marginTop:4}}>Read more</div>
                  )}
                  {bioExpanded && (
                    <div onClick={()=>setBioExpanded(false)}
                      style={{fontSize:12,color:GOLD,fontWeight:600,cursor:"pointer",marginTop:4}}>Show less</div>
                  )}
                </div>
            </div>
          )}

          <div style={{height:1,background:dark?"rgba(255,255,255,0.06)":C.border,margin:"18px 0"}}/>

          <div style={{marginBottom:6}}>
            <div style={{fontSize:10,fontWeight:700,color:dark?GOLD+"99":"#999",
              letterSpacing:1.2,textTransform:"uppercase",marginBottom:12}}>Socials</div>
            {[
              {label:"Instagram", field:"ig",  icon:"\uD83D\uDCF7", prefix:"@"},
              {label:"X",         field:"x",   icon:"\uD835\uDD4F",  prefix:"@"},
              {label:"Website",   field:"website", icon:"\uD83C\uDF10", prefix:""},
            ].map(({label,field,icon,prefix})=>{
              if(!profile[field]) return null;
              return (
                <div key={field} style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                  <span style={{fontSize:14,width:20,flexShrink:0}}>{icon}</span>
                  <span style={{fontSize:13,color:GOLD,fontWeight:500}}>{prefix}{profile[field]}</span>
                </div>
              );
            })}
          </div>

          <div style={{height:1,background:dark?"rgba(255,255,255,0.06)":C.border,margin:"18px 0"}}/>

          <div style={{marginBottom:18}}>
            <div style={{fontSize:10,fontWeight:700,color:dark?GOLD+"99":"#999",
              letterSpacing:1.2,textTransform:"uppercase",marginBottom:12}}>Stats</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              {profileStats.map(([l,v,onTap])=>(
                <div key={l} onClick={onTap||undefined} className={onTap?"btn":undefined}
                  style={{background:dark?"rgba(255,255,255,0.04)":"rgba(0,0,0,0.03)",cursor:onTap?"pointer":"default",
                  borderRadius:10,padding:"12px 14px",border:`1px solid ${dark?"#151515":C.border}`}}>
                  {profileStatsLoaded?(
                    <div style={{fontSize:20,fontWeight:800,color:dark?GOLD:"#111",marginBottom:2}}>{v||"0"}</div>
                  ):(
                    <div style={{width:36,height:20,borderRadius:5,background:dark?"rgba(255,255,255,0.06)":"#e8e8e8",overflow:"hidden",position:"relative",marginBottom:2}}>
                      <div style={{position:"absolute",inset:0,background:`linear-gradient(90deg,transparent,${dark?"rgba(255,255,255,0.06)":"rgba(255,255,255,0.7)"},transparent)`,animation:"bamShimmer 1.5s ease-in-out infinite"}}/>
                    </div>
                  )}
                  <div style={{fontSize:10,color:C.textDim,fontWeight:600,textTransform:"uppercase",letterSpacing:.8}}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          {dismissBanner&&!(dismissedBanners||[]).includes("profile-cert")&&(
            <div style={{background:dark?"rgba(226,221,159,0.06)":"rgba(226,221,159,0.12)",border:`1px solid ${GOLD}44`,
              borderRadius:10,padding:"14px 16px",marginTop:18,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap",position:"relative"}}>
              <div className="btn" onClick={()=>dismissBanner("profile-cert")} style={{position:"absolute",top:6,right:10,fontSize:12,color:C.textDim,cursor:"pointer"}}>×</div>
              <div style={{flex:1,minWidth:160}}>
                <div style={{fontSize:13,color:C.text,fontWeight:600}}>Take your coaching further</div>
                <div style={{fontSize:11,color:C.textDim,marginTop:2}}>Get BAM Certified</div>
              </div>
              <a href="https://byanymeanscoaches.com/certification" target="_blank" rel="noopener noreferrer"
                style={{padding:"7px 16px",borderRadius:7,background:GOLD,color:"#111",fontSize:11,fontWeight:800,textDecoration:"none",flexShrink:0}}>
                Get Certified
              </a>
            </div>
          )}

          <div style={{height:1,background:dark?"rgba(255,255,255,0.06)":C.border,margin:"18px 0"}}/>

          <div>
            <div style={{fontSize:10,fontWeight:700,color:dark?GOLD+"99":"#999",
              letterSpacing:1.2,textTransform:"uppercase",marginBottom:12}}>Account</div>

            <div style={{marginBottom:14}}>
              <div style={{fontSize:10,fontWeight:600,color:C.textDim,marginBottom:5,textTransform:"uppercase",letterSpacing:.8}}>Email</div>
              {editingEmail?(
                <div style={{display:"flex",gap:8,alignItems:"flex-start"}}>
                  <div style={{flex:1}}>
                    <input value={emailDraft} onChange={e=>{setEmailDraft(e.target.value);setEmailError("");}}
                      style={inputStyle(!!emailError)} autoFocus={!(typeof window!=="undefined"&&(navigator.maxTouchPoints||0)>0)}/>
                    {emailError&&<div style={{fontSize:10,color:"#E06060",marginTop:2}}>{emailError}</div>}
                  </div>
                  <button onClick={saveEmail}
                    style={{padding:"8px 14px",borderRadius:7,background:GOLD,border:"none",color:"#111",
                      fontSize:12,fontWeight:700,cursor:"pointer",flexShrink:0}}>Save</button>
                  <button onClick={()=>{setEditingEmail(false);setEmailDraft(profile.email||"");setEmailError("");}}
                    style={{padding:"8px 10px",borderRadius:7,border:`1px solid ${dark?"#444":C.border}`,
                      background:"transparent",color:C.textDim,fontSize:12,cursor:"pointer",flexShrink:0}}>×</button>
                </div>
              ):(
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:13,color:C.text}}>{profile.email||"Not set"}</span>
                  <Pen size={12} color={C.textDim} style={{cursor:"pointer"}} onClick={()=>{setEmailDraft(profile.email||"");setEditingEmail(true);}}/>
                  {emailSuccess&&<span style={{fontSize:11,color:GOLD,fontWeight:600,animation:"bamFadeOut 2s forwards"}}>Email updated</span>}
                </div>
              )}
            </div>

            <button onClick={()=>setPwModal(true)}
              style={{padding:"10px 18px",borderRadius:9,border:`1px solid ${dark?"#444":C.border}`,
                background:"transparent",color:C.text,fontSize:13,fontWeight:700,cursor:"pointer",width:"100%",textAlign:"left"}}>
              Change Password
            </button>
          </div>

        </div>
      </div>

      {pwModal&&(
        <div onClick={e=>e.stopPropagation()}
          style={{position:"fixed",inset:0,zIndex:3500,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(2px)"}}>
          <div style={{background:dark?"#111111":"#fff",borderRadius:14,padding:"28px 28px",width:360,maxWidth:"90vw",
            border:`1px solid ${dark?"rgba(255,255,255,0.06)":C.border}`,boxShadow:`0 20px 60px rgba(0,0,0,0.5)`}}>
            {pwSuccess?(
              <div style={{textAlign:"center",padding:"20px 0"}}>
                <div style={{fontSize:18,fontWeight:800,color:GOLD,marginBottom:8}}>Password updated successfully</div>
              </div>
            ):(
              <>
                <div style={{fontSize:16,fontWeight:800,color:C.text,marginBottom:20}}>Change Password</div>
                {[
                  {label:"Current Password",key:"current",err:pwErrors.current,ac:"current-password"},
                  {label:"New Password",key:"newPw",err:pwErrors.newPw,ac:"new-password"},
                  {label:"Confirm New Password",key:"confirm",err:pwErrors.confirm,ac:"new-password"},
                ].map(({label,key,err,ac})=>(
                  <div key={key} style={{marginBottom:14}}>
                    <div style={{fontSize:11,fontWeight:600,color:C.textDim,marginBottom:4}}>{label}</div>
                    <div style={{position:"relative"}}>
                      <input type={showPw?"text":"password"} value={pwForm[key]}
                        onChange={e=>setPwForm(p=>({...p,[key]:e.target.value}))}
                        autoComplete={ac}
                        style={{...inputStyle(!!err),width:"100%",paddingRight:40}}/>
                      <button type="button" onClick={()=>setShowPw(v=>!v)} aria-label={showPw?"Hide passwords":"Show passwords"}
                        style={{position:"absolute",right:2,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:C.textDim,cursor:"pointer",padding:8,display:"flex"}}>
                        {showPw?<EyeOff size={15}/>:<Eye size={15}/>}
                      </button>
                    </div>
                    {err&&<div style={{fontSize:10,color:"#E06060",marginTop:2}}>{err}</div>}
                  </div>
                ))}
                <div style={{display:"flex",gap:8,marginTop:8}}>
                  <button onClick={submitPw}
                    style={{flex:1,padding:"10px",borderRadius:8,background:GOLD,border:"none",color:"#111",fontWeight:800,fontSize:13,cursor:"pointer"}}>
                    Update Password
                  </button>
                  <button onClick={()=>{setPwModal(false);setPwForm({current:"",newPw:"",confirm:""});setPwErrors({});}}
                    style={{padding:"10px 16px",borderRadius:8,border:`1px solid ${dark?"#444":C.border}`,
                      background:"transparent",color:C.textDim,fontSize:13,cursor:"pointer"}}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
